import { Icon, Typography } from '@smartgift/frontend-toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AUTOMATION_STATUSES } from '@hero/constants';
import { AutomationStatusSummary, CostSummary, ToggleMenuGroup } from '@hero/molecules';
import {
  AutomationDetailOverviewAccordion,
  AutomationDetailRecipientsAccordion,
  AutomationReviewExperienceAccordion,
  AutomationReviewGiftsAccordion
} from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useDuplicateAutomation,
  useGetAutomation,
  useGetAutomationSpendingSummary,
  useUpdateAutomation
} from '@hero/services/order-service';
import { useGetAcceptanceRate } from '@hero/services/order-service/hook';
import { getAverageTimeFromSeconds } from '@hero/utils/timeUtils';

import * as S from './style';

function OrderSummary({ setActiveTab }) {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: automation, isSuccess: isAutomationSuccess } = useGetAutomation({ code });
  const { mutateAsync: updateAutomation } = useUpdateAutomation();
  const {
    data: automationSpendingSummary,
    isFetching: isFetchingSpendingSummary,
    isLoading: isLoadingSpendingSummary,
    isError: isErrorSpendingSummary
  } = useGetAutomationSpendingSummary({
    code,
    products: automation?.products
  });
  const { data: automationAcceptanceRate } = useGetAcceptanceRate(code, {
    filters: {
      associationType: 'automation'
    }
  });
  const { mutateAsync: duplicateAutomation } = useDuplicateAutomation();

  const cardList = [
    {
      id: 'overview',
      icon: <Icon name="order" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('overview')}</Typography>,
      children: () => <AutomationDetailOverviewAccordion automation={automation} />
    },
    {
      id: 'gifts',
      icon: <Icon name="gift-box" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('gifts')}</Typography>,
      children: () => (
        <AutomationReviewGiftsAccordion automation={automation} onUpdate={updateAutomation} />
      )
    },
    {
      id: 'contacts',
      icon: <Icon name="contacts" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('recipients')}</Typography>,
      children: () => (
        <AutomationDetailRecipientsAccordion
          automation={automation}
          onUpdate={updateAutomation}
          setActiveTab={setActiveTab}
        />
      )
    },
    {
      id: 'experience',
      icon: <Icon name="smartphone" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('experience')}</Typography>,
      children: () => (
        <AutomationReviewExperienceAccordion
          automation={automation}
          isAutomationSuccess={isAutomationSuccess}
          onUpdate={updateAutomation}
        />
      )
    }
  ];

  return (
    <S.OrderSummaryWrapper>
      <ToggleMenuGroup
        multiMenuActive
        list={cardList.filter(card => card.id)}
        defaultActiveMenus={['overview']}
      />
      <S.RightColumn>
        <CostSummary
          {...(automationSpendingSummary || {})}
          status={automation?.status}
          featureType="automation"
          isLoadingSpendingSummary={isFetchingSpendingSummary || isLoadingSpendingSummary}
          isError={isErrorSpendingSummary}
        />
        <AutomationStatusSummary
          duplicateText={automation?.name}
          feature={automation}
          spendingSummary={automationSpendingSummary}
          analytics={automationAcceptanceRate?.acceptanceRates}
          averageAcceptanceRate={getAverageTimeFromSeconds(
            automationAcceptanceRate?.avgAcceptanceTimeInSeconds
          )}
          setActiveTab={setActiveTab}
          onDeactivate={() => {
            updateAutomation({
              params: { code },
              body: {
                status: AUTOMATION_STATUSES.deactivated
              },
              successMessage: t('automationUpdatedSuccessfully')
            });
          }}
          onReactivate={({ formValues }) => {
            updateAutomation({
              params: { code },
              body: {
                settings: {
                  occasionScheduleDuration: {
                    unit: 'day',
                    value: Number(formValues.occasionScheduleDuration)
                  },
                  acceptanceDurationInHours: Number(formValues.redemptionWindow)
                },
                status: AUTOMATION_STATUSES.active
              },
              successMessage: t('automationUpdatedSuccessfully')
            });
          }}
          onDuplicate={async formValues => {
            const response = await duplicateAutomation({
              code,
              body: formValues
            });

            navigate(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE(response.code));
          }}
          giftCount={automation?.products?.length}
          contactType={automation?.contacts?.ids?.length > 0 ? 'list' : 'tags'}
          giftMessage={automation?.experienceSettings?.messageParams?.message}
        />
      </S.RightColumn>
    </S.OrderSummaryWrapper>
  );
}

export { OrderSummary };
